import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import './TuteeDashboard.css';

const TuteeDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [subjectPresence, setSubjectPresence] = useState({});
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  // ── Notification state ──
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Modal states
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // ── View Details reveal state ──
  const [revealedSessionId, setRevealedSessionId] = useState(null);
  const revealTimeoutRef = useRef(null);

  const API_BASE = 'https://mykaddle-api.onrender.com';

  const fetchTuteeSubjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/api/tutee/subjects/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubjects(res.data || []);
    } catch (err) {
      console.error('Failed to load subjects', err);
    }
  }, []);

  const fetchTuteeSessions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/tutee/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch sessions', err);
      setSessions([]);
    }
  }, []);

  // ── Fetch notifications ──
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_BASE}/api/notifications?limit=50`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  }, []);

  // ── Fetch unread count (lightweight poll) ──
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_BASE}/api/notifications/unread-count`, { headers });
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/auth'); return; }
    setUser(JSON.parse(storedUser));
    fetchTuteeSubjects();
    fetchTuteeSessions();
    fetchUnreadCount();
  }, [navigate, fetchTuteeSubjects, fetchTuteeSessions, fetchUnreadCount]);

  // Poll unread count every 30s
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Socket.io presence
  useEffect(() => {
    const socket = io('https://mykaddle-api.onrender.com');
    socket.on('subjectPresenceUpdate', ({ subjectId, count }) => {
      setSubjectPresence(prev => ({ ...prev, [subjectId]: count }));
    });
    return () => socket.disconnect();
  }, []);

  // ── Navigation handlers ──
  const goToDiscover = () => navigate('/tutee/dashboard/Discover');
  const goToSupport = () => navigate('/dashboard/support');
  const goToSubscriptions = () => navigate('/dashboard/subscriptions');
  const goTottRequests = () => navigate('/tutee/Requests');

  // ── Logout handlers ──
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleLogoutCancel = () => setShowLogoutModal(false);
  const handleLogoutConfirm = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/auth');
  };

  // ── Date chip helpers ──
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();

  const getDateChips = () => {
    const chips = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dayLabel = days[d.getDay()];
      const dayNum = d.getDate();
      chips.push({ key: `${dayLabel} ${dayNum}`, label: dayLabel, num: dayNum, isToday: i === 0 });
    }
    return chips;
  };

  useEffect(() => {
    if (!selectedDateKey) {
      const chips = getDateChips();
      if (chips.length > 0) setSelectedDateKey(chips[0].key);
    }
  }, []);

  // ── Sessions for selected date ──
  const getSessionsForDate = (dateKey) => {
    if (!dateKey) return [];
    return sessions.filter(s => {
      const start = new Date(s.start_time);
      const dayLabel = days[start.getDay()];
      return `${dayLabel} ${start.getDate()}` === dateKey;
    });
  };

  const formatRemainingTime = (start, end) => {
    const now = new Date();
    const startTime = new Date(start);
    const endTime = new Date(end);
    if (now >= endTime) return null;
    if (now >= startTime) return "Started";
    let diff = startTime - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    diff -= days * (1000 * 60 * 60 * 24);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  // ── View Details handler ──
  const handleViewDetails = (session, index) => {
    const id = session._id || index;
    if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);
    setRevealedSessionId(id);
    revealTimeoutRef.current = setTimeout(() => {
      setRevealedSessionId(null);
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);
    };
  }, []);

  // ── Time-based greeting ──
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // ── Open notification modal & fetch full list ──
  const handleBellClick = () => {
    fetchNotifications();
    setShowNotifModal(true);
  };

  // ── Mark single notification as read ──
  const handleNotificationClick = async (notif) => {
    if (notif.read) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_BASE}/api/notifications/${notif.id}/read`, {}, { headers });
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notif.id ? { ...n, read: true, readAt: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Navigate to action URL if provided
      if (notif.actionUrl) {
        setShowNotifModal(false);
        navigate(notif.actionUrl);
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const dateChips = getDateChips();
  const currentSessions = getSessionsForDate(selectedDateKey);

  if (!user) return <div className="ttd-loading">Loading...</div>;

  return (
    <div className="ttd-dashboard-container">
      <div className="ttd-almighty-wrapper">

        {/* ── Sessions Panel (Left Sidebar) ── */}
        <aside className="ttd-sessions-panel">
          <div className="ttd-sessions-header">
            <span className="ttd-sessions-title">
              <svg className="ttd-sessions-title-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                <path d="M0 0h256v256H0z" fill="none" />
                <path fill="currentColor" d="M208 36h-28V24a4 4 0 0 0-8 0v12H84V24a4 4 0 0 0-8 0v12H48a12 12 0 0 0-12 12v160a12 12 0 0 0 12 12h160a12 12 0 0 0 12-12V48a12 12 0 0 0-12-12M48 44h28v12a4 4 0 0 0 8 0V44h88v12a4 4 0 0 0 8 0V44h28a4 4 0 0 1 4 4v36H44V48a4 4 0 0 1 4-4m160 168H48a4 4 0 0 1-4-4V92h168v116a4 4 0 0 1-4 4m-72-80a8 8 0 1 1-8-8a8 8 0 0 1 8 8m44 0a8 8 0 1 1-8-8a8 8 0 0 1 8 8m-88 40a8 8 0 1 1-8-8a8 8 0 0 1 8 8m44 0a8 8 0 1 1-8-8a8 8 0 0 1 8 8m44 0a8 8 0 1 1-8-8a8 8 0 0 1 8 8" />
              </svg>
              Sessions
            </span>
          </div>

          <div className="ttd-date-strip">
            {dateChips.map((chip) => (
              <div
                key={chip.key}
                className={`ttd-date-chip ${selectedDateKey === chip.key ? 'ttd-date-chip-today' : ''}`}
                onClick={() => setSelectedDateKey(chip.key)}
              >
                <span className="ttd-day-label">{chip.label}</span>
                <span className="ttd-day-num">{chip.num}</span>
              </div>
            ))}
          </div>

          <div className="ttd-session-list">
            {currentSessions.length === 0 ? (
              <div className="ttd-empty-state">
              </div>
            ) : (
              currentSessions.map((session, index) => {
                const remaining = formatRemainingTime(session.start_time, session.end_time);
                const colorClass = index % 2 === 0 ? 'ttd-card-lime' : 'ttd-card-pink';
                const cardId = session._id || index;
                const isRevealed = revealedSessionId === cardId;

                if (isRevealed) {
                  return (
                    <div key={cardId} className="ttd-session-card ttd-card-revealed">
                      <div className="ttd-revealed-details">
                        <div className="ttd-revealed-row"><strong>Subject:</strong> {session.subject_name || 'General'}</div>
                        <div className="ttd-revealed-row"><strong>Topic:</strong> {session.topic || 'Untitled Session'}</div>
                        <div className="ttd-revealed-row"><strong>Description:</strong> {session.description || 'No description'}</div>
                        <div className="ttd-revealed-row"><strong>Start:</strong> {new Date(session.start_time).toLocaleString()}</div>
                        <div className="ttd-revealed-row"><strong>End:</strong> {new Date(session.end_time).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={cardId} className={`ttd-session-card ${colorClass}`}>
                    <div className="ttd-card-inner">
                      <span className="ttd-sc-subject">{session.subject_name || 'General'}</span>
                      <span className="ttd-sc-topic">{session.topic || 'Untitled Session'}</span>
                      <div className="ttd-sc-time-row">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                            <path d="M10 2h4m-2 12l3-3"/><circle cx="12" cy="14" r="8"/>
                          </g>
                        </svg>
                        {remaining || 'Ended'}
                      </div>
                    </div>
                    <div className="ttd-card-footer">
                      <span className="ttd-view-details" onClick={() => handleViewDetails(session, index)}>View Details</span>
                      <button
                        className="ttd-sc-link-btn"
                        onClick={() => window.open(session.class_link || '#', '_blank')}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                        Join Class
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* ── Main Content ── */}
        <div className="ttd-main-content">
          <div className="ttd-top-bar">
            <svg
              className="ttd-bell-icon"
              onClick={handleBellClick}
              style={{ color: unreadCount > 0 ? '#32cd32' : 'currentColor' }}
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 32 32"
            >
              <path d="M0 0h32v32H0z" fill="none"/>
              <path fill="currentColor" d="M6.5 5h19A4.5 4.5 0 0 1 30 9.5v13a4.5 4.5 0 0 1-4.5 4.5h-19A4.5 4.5 0 0 1 2 22.5v-13A4.5 4.5 0 0 1 6.5 5M29 10.206V9.5A3.5 3.5 0 0 0 25.5 6h-19A3.5 3.5 0 0 0 3 9.5v.706l13 7.222zM3 11.35V22.5A3.5 3.5 0 0 0 6.5 26h19a3.5 3.5 0 0 0 3.5-3.5V11.35l-12.757 7.087a.5.5 0 0 1-.486 0z"/>
            </svg>
          </div>

          <div className="ttd-welc-name">{getGreeting()}, {user.firstName} {user.lastName}</div>

          <div className="ttd-group-items">
            <div className="ttd-main-groups">
              <div className="ttd-main-groups-header">Main Groups</div>
              <div className="ttd-main-groups-items">
                {subjects
                  .filter(sub => !sub.isRequestBased)
                  .slice(0, 3)
                  .map((sub, index) => {
                    const onlineCount = subjectPresence[sub.subjectId] || 0;
                    const isActive = onlineCount > 0;
                    const groupClass = `ttd-subject${index + 1}`;

                    return (
                      <div key={sub.subjectId} className={groupClass} onClick={() => navigate(`/portal/${sub.subjectId}`)}>
                        <div className="ttd-subject-row">
                          <div className="ttd-subject-info">
                            <span className="ttd-mgsn">{sub.subjectDisplayName}</span>
                            <span className="ttd-active-status">
                              <span className={isActive ? 'ttd-green-dot' : 'ttd-red-dot'}></span>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="ttd-subject-count">{sub.tuteeCount || 0}</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {subjects.length === 0 && (
                <div className="ttd-main-groups-empty">
                  <div className="ttd-main-groups-empty-text">No subject groups to display.<br/>Find a tutor and join their group.</div>
                </div>
              )}
            </div>

            <div className="ttd-dash-private-requests">
              <div className="ttd-dash-private-requets-header">All Requests</div>
              <div className="ttd-dash-private-requests-empty">
                <div className="ttd-dash-private-requests-empty-text">Click manage requests to see all requests.</div>
              </div>
              <div className="ttd-requets-footer" onClick={goTottRequests}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20">
                  <path d="M0 0h20v20H0z" fill="none"/>
                  <g fill="none" stroke="currentColor" strokeLinecap="round">
                    <circle cx="9.5" cy="5.5" r="3"/>
                    <path d="M15 16.5v-2c0-3.098-2.495-6-5.5-6S4 11.402 4 14.5v2"/>
                  </g>
                </svg>
                Manage Requests
              </div>
            </div>
          </div>

          <div className="ttd-bottom-boxes">
            <div className="ttd-wallet-box">
              <div className="ttd-wallet-box-header">Tutor Discovery</div>
              <div className="ttd-wallet-box-subheader">Find tutors for your subject needs from the MyKaddle network. The steps are as follows</div>
              <div className="ttd-wallet-steps">
                <div className="ttd-wallet-step">
                  <div className="ttd-wallet-step-circle ttd-step1">1</div>
                  <div className="ttd-wallet-step-text">
                    <strong>Discover Tutors</strong>
                    Click <strong style={{display: 'inline'}}>Search</strong> below to open the Discover page and find your preferred tutor.
                  </div>
                </div>
                <div className="ttd-wallet-step">
                  <div className="ttd-wallet-step-circle ttd-step2">2</div>
                  <div className="ttd-wallet-step-text">
                    <strong>Choose Your Learning Style</strong>
                    Choose between group-based tutoring or private 1-on-1 tutoring.
                  </div>
                </div>
                <div className="ttd-wallet-step">
                  <div className="ttd-wallet-step-circle ttd-step3">3</div>
                  <div className="ttd-wallet-step-text">
                    <strong>Find &amp; Connect</strong>
                    Search using a tutor name, subject, qualification, institution or browse all available tutors. Join a group or send a private request to get started.
                  </div>
                </div>
              </div>
              <div className="ttd-wallet-box-footer" onClick={goToDiscover}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none"/>
                  <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
                </svg>
                Search Tutors
              </div>
            </div>

            <div className="ttd-account-box">
              <div className="ttd-account-box-header">Subscriptions & Help</div>
              <div className="ttd-account-box-subheader">Take control over all your subscriptions to all subjects or reach out whenever you need a hand.</div>
              <div className="ttd-account-items">
                <div className="ttd-account-item" onClick={goToSubscriptions}>
                  <span className="ttd-account-item-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m8.068 2.725l-.172.137c-.185.147-.278.22-.37.267c-.319.16-.7.139-1-.055c-.086-.056-.17-.14-.337-.306c-.4-.396-.6-.595-.751-.668a1.003 1.003 0 0 0-1.383.568C4 2.826 4 3.106 4 3.667v17.031c0 .257 0 .385.012.46c.114.736.974 1.09 1.58.649c.061-.045.153-.136.336-.317c.115-.114.173-.172.227-.216a1.51 1.51 0 0 1 1.779-.098c.059.038.123.089.25.19l.136.108c.23.183.346.274.461.336c.448.243.99.243 1.438 0c.115-.062.23-.153.46-.336l.071-.056c.297-.235.445-.353.598-.426a1.51 1.51 0 0 1 1.304 0c.153.073.301.19.598.426l.07.056c.23.183.346.274.461.336c.448.243.99.243 1.438 0c.115-.062.23-.153.46-.336l.137-.108c.127-.101.191-.152.25-.19a1.51 1.51 0 0 1 1.779.098c.054.044.112.101.227.216c.183.181.275.272.336.317c.606.44 1.467.087 1.58-.649c.012-.075.012-.203.012-.46V3.667c0-.561 0-.841-.055-.999a1.003 1.003 0 0 0-1.383-.568c-.15.073-.35.272-.75.668c-.168.166-.252.25-.339.306a1.01 1.01 0 0 1-1 .055c-.091-.047-.184-.12-.369-.267l-.172-.137c-.47-.373-.706-.56-.963-.643c-.304-.1-.634-.1-.938 0c-.257.084-.492.27-.963.643L13 2.78c-.357.284-.536.425-.734.48a1 1 0 0 1-.532 0c-.198-.055-.377-.196-.734-.48l-.068-.054c-.47-.373-.706-.56-.962-.643a1.5 1.5 0 0 0-.94 0c-.256.084-.491.27-.962.643M8 12h8M8 8h4m-4 8h8" />
                    </svg>
                  </span>
                  <span className="ttd-account-item-label">Subscriptions</span>
                  <span className="ttd-account-item-arrow">›</span>
                </div>
                <div className="ttd-account-item" onClick={goToSupport}>
                  <span className="ttd-account-item-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 36 36">
                      <path d="M0 0h36v36H0z" fill="none" />
                      <path fill="currentColor" d="M18 2a16 16 0 1 0 16 16A16 16 0 0 0 18 2m0 30a14 14 0 1 1 14-14a14 14 0 0 1-14 14" />
                      <path fill="currentColor" d="M18.29 8.92a7.38 7.38 0 0 0-5.72 2.57a1 1 0 0 0-.32.71a.92.92 0 0 0 .95.92a1.08 1.08 0 0 0 .71-.29a5.7 5.7 0 0 1 4.33-2c2.36 0 3.83 1.52 3.83 3.41v.05c0 2.21-1.76 3.44-4.54 3.65a.8.8 0 0 0-.76.92s0 2.32 0 2.75a1 1 0 0 0 1 .9h.11a1 1 0 0 0 .9-1v-2.06c3-.42 5.43-2 5.43-5.28v-.05c-.03-3-2.37-5.2-5.92-5.2" />
                      <circle cx="17.78" cy="26.2" r="1.25" fill="currentColor" />
                      <path fill="none" d="M0 0h36v36H0z" />
                    </svg>
                  </span>
                  <span className="ttd-account-item-label">Support</span>
                  <span className="ttd-account-item-arrow">›</span>
                </div>
              </div>
            </div>
          </div>

          <svg className="ttd-back-arrow-icon" onClick={handleLogoutClick} xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 512 512">
            <path d="M0 0h512v512H0z" fill="none"/>
            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="m112 352l-64-64l64-64"/>
            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M64 288h294c58.76 0 106-49.33 106-108v-20"/>
          </svg>
        </div>
      </div>

      {/* ── Notifications Modal ── */}
      {showNotifModal && (
        <div className="ttd-modal-backdrop ttd-modal-open" onClick={() => setShowNotifModal(false)}>
          <div className="ttd-modal" onClick={e => e.stopPropagation()}>
            <button className="ttd-modal-close" onClick={() => setShowNotifModal(false)}>✕</button>
            <div className="ttd-modal-header">
              <div className="ttd-modal-title">Notifications</div>
              <div className="ttd-modal-sub">Stay updated with your latest sessions, requests, and payments.</div>
            </div>
            <div className="ttd-modal-body-content">
              {notifications.length === 0 ? (
                <div className="ttd-notif-item">
                  <div className="ttd-notif-info">
                    <div className="ttd-notif-title">No notifications yet</div>
                    <div className="ttd-notif-sub">We'll notify you when something happens.</div>
                  </div>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`ttd-notif-item ${!notif.read ? 'ttd-notif-unread' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <div className="ttd-notif-info">
                      <div className="ttd-notif-title">{notif.title}</div>
                      <div className="ttd-notif-sub">{notif.subtitle}</div>
                    </div>
                    <div className="ttd-notif-time">{notif.timeAgo}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Logout Modal ── */}
      {showLogoutModal && (
        <div className="td-modal-backdrop td-modal-open" onClick={handleLogoutCancel}>
          <div className="td-modal" style={{ width: '420px' }} onClick={e => e.stopPropagation()}>
            <button className="td-modal-close" onClick={handleLogoutCancel}>✕</button>
            <div className="td-logout-modal-content">
              <div className="td-logout-header-row">
                <div className="td-logout-header-text">
                  <div className="td-modal-title">Confirm Logout</div>
                  <div className="td-modal-sub">Are you sure you want to log out of your account?</div>
                </div>
              </div>
              <div className="td-modal-actions">
                <button className="td-btn-cancel" onClick={handleLogoutCancel}>Cancel</button>
                <button className="td-btn-confirm-red" onClick={handleLogoutConfirm}>Yes, log out</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuteeDashboard;